/**
 * Finding one line among many — the RFQ builder's table and the record-quotation dialog.
 *
 * ---- Why all words, in any order ----
 *
 * A purchaser recording a phone quote knows the item, not its position: on a 79-line enquiry they
 * want the two the supplier actually priced. Typing "coverall tape" and being told there is no
 * match, because the line reads "COVERALL WITH REFLECTIVE TAPE", would be worse than no search at
 * all — so every word must appear somewhere, and their order is not a constraint.
 *
 * ---- Three fields, because there are three ways people refer to a line ----
 *
 * The customer's own words (`raw_text`), our catalogue code (`item_code`), and the customer's
 * serial (`cust_sno`) — which is what they will quote back down the phone. All are searched
 * together rather than through a "search by" selector nobody would set correctly.
 *
 * An EMPTY query matches everything, so clearing the box restores the list rather than emptying it.
 */

export interface SearchableLine {
  item_code?: string | null
  raw_text?: string | null
  cust_sno?: string | null
}

export function matchesLineSearch(line: SearchableLine, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter((w) => w !== '')
  if (words.length === 0) return true

  const haystack = [line.raw_text, line.item_code, line.cust_sno]
    .filter((v): v is string => typeof v === 'string' && v !== '')
    .join(' ')
    .toLowerCase()

  return words.every((word) => haystack.includes(word))
}
